'use client'

import getComputedPrice from '@/app/[locale]/_service/fare.service'
import { IRedux } from '@/app/[locale]/_store/types'
import { useQuery } from '@tanstack/react-query'
import { useMemo } from 'react'
import { useSelector } from 'react-redux'
import { shallowEqual } from 'react-redux'
import { resolvePaymentTypeId } from '@/app/[locale]/_utils/aggregator-context'

export interface UseDeliveryPriceOptions {
  /** true = homepage (sends is_home_page_delivery: true), false/omit = cart/checkout */
  isHomePage?: boolean
  /** when false, skips fetching (e.g. when settings hide delivery cost). Omit = fetch when delivery */
  enabled?: boolean
}

export function useDeliveryPrice(
  orderTotal: number,
  options: UseDeliveryPriceOptions = {}
) {
  const { isHomePage = false, enabled: enabledOption = false } = options

  const { auth, cart, checkout, order_type, common, session } = useSelector(
    (state: IRedux) => ({
      auth: state.auth,
      checkout: state.checkout,
      cart: state.cart.cart,
      order_type: state.session.order_type,
      common: state.common,
      session: state.session,
    }),
    shallowEqual
  )

  const activePoint = useMemo(() => {
    if (order_type?.type !== 'delivery' || !common?.points?.length) return null
    const active = common.points.find((p) => p?.isActive)
    if (!active?.location?.length) return null
    return {
      lat: active.location[0] ?? 0,
      long: active.location[1] ?? 0,
      address: active.address || '',
    }
  }, [common?.points, order_type?.type])

  const branch = common?.branch
  const deliveryZoneId = common?.deliveryZone?.id || ''
  const platform_id = session?.platform_id || ''
  const paymentTypeId = resolvePaymentTypeId(checkout, auth)

  const shouldFetch =
    order_type?.type === 'delivery' &&
    cart?.length > 0 &&
    orderTotal > 0 &&
    branch?.id &&
    activePoint?.lat &&
    activePoint?.long

  const { data, isLoading } = useQuery({
    queryKey: [
      'delivery-price',
      isHomePage ? 'home' : 'cart',
      orderTotal,
      branch?.id,
      activePoint?.lat,
      activePoint?.long,
      deliveryZoneId,
      paymentTypeId,
      String(cart?.map((e) => `${e.product_id}:${e.quantity}`).join(',')),
    ],
    queryFn: async () => {
      const res = await getComputedPrice({
        branch_id: branch?.id,
        lat: activePoint!.lat,
        long: activePoint!.long,
        order_price: orderTotal,
        delivery_zone_id: deliveryZoneId,
        ...(isHomePage && { is_home_page_delivery: true }),
        order_details: {
          branch_id: branch?.id || '',
          client_name: '',
          client_phone_number: '',
          description: '',
          to_address: activePoint!.address,
          payment_type_id: paymentTypeId,
          to_location: {
            lat: activePoint!.lat,
            long: activePoint!.long,
          },
          products:
            cart?.map((el) => ({
              name: el.product_name,
              price: el.price_with_discount ?? el.price ?? 0,
              product_id: el.product_id,
              quantity: el.quantity,
            })) ?? [],
          source_id: platform_id,
        },
      })
      return res?.data
    },
    enabled: Boolean(shouldFetch && enabledOption),
  })

  return {
    deliveryPrice: data?.price ?? 0,
    isLoading: Boolean(shouldFetch && enabledOption) && isLoading,
  }
}
